import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './auth-context'
import { useTheme as useThemeContext } from './theme-context'

export type WeekStartsOn = 'sunday' | 'monday'
export type Theme = 'light' | 'dark' | 'system'

interface SettingsContextType {
  timezone: string
  weekStartsOn: WeekStartsOn
  theme: Theme
  loading: boolean
  setTimezone: (timezone: string) => void
  setWeekStartsOn: (weekStartsOn: WeekStartsOn) => void
  setTheme: (theme: Theme) => void
  updateSettings: (settings: Partial<{ timezone: string; weekStartsOn: WeekStartsOn; theme: Theme }>) => void
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

const STORAGE_KEY = 'calintent-settings'

// Get browser's timezone
const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    // Fallback to UTC if timezone detection fails
    return 'UTC'
  }
}

// Get default settings
const getDefaultSettings = () => ({
  timezone: getBrowserTimezone(),
  weekStartsOn: 'monday' as WeekStartsOn,
  theme: 'system' as Theme,
})

// Load settings from localStorage
const loadSettings = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        ...getDefaultSettings(),
        ...parsed,
      }
    }
  } catch (error) {
    console.warn('Failed to load settings from localStorage:', error)
  }
  return getDefaultSettings()
}

// Save settings to localStorage
const saveSettings = (settings: { timezone: string; weekStartsOn: WeekStartsOn }) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (error) {
    console.warn('Failed to save settings to localStorage:', error)
  }
}

// Load settings from database
const loadSettingsFromDB = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('timezone, week_starts_on, theme')
      .eq('id', userId)
      .single()

    if (error) {
      console.warn('Failed to load settings from database:', error)
      return null
    }

    return {
      timezone: data.timezone || getBrowserTimezone(),
      weekStartsOn: (data.week_starts_on as WeekStartsOn) || 'monday',
      theme: (data.theme as Theme) || 'system',
    }
  } catch (error) {
    console.warn('Failed to load settings from database:', error)
    return null
  }
}

// Save settings to database
const saveSettingsToDB = async (userId: string, settings: { timezone: string; weekStartsOn: WeekStartsOn; theme: Theme }) => {
  try {
    const { error } = await supabase
      .from('users')
      .update({
        timezone: settings.timezone,
        week_starts_on: settings.weekStartsOn,
        theme: settings.theme,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)

    if (error) {
      console.warn('Failed to save settings to database:', error)
      throw error
    }
  } catch (error) {
    console.warn('Failed to save settings to database:', error)
    throw error
  }
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const { setTheme: setThemeContext } = useThemeContext()
  const [settings, setSettings] = useState(() => loadSettings())
  const [loading, setLoading] = useState(false)

  // Load settings from database when user changes
  useEffect(() => {
    const loadUserSettings = async () => {
      if (!user?.id) {
        // No user, use localStorage defaults
        setSettings(loadSettings())
        return
      }

      setLoading(true)
      try {
        const dbSettings = await loadSettingsFromDB(user.id)
        if (dbSettings) {
          setSettings(dbSettings)
          // Also save to localStorage as backup
          saveSettings(dbSettings)
        } else {
          // Fallback to localStorage if DB fails
          setSettings(loadSettings())
        }
      } catch (error) {
        console.warn('Failed to load user settings:', error)
        setSettings(loadSettings())
      } finally {
        setLoading(false)
      }
    }

    loadUserSettings()
  }, [user?.id])

  // Save to localStorage whenever settings change (as backup)
  useEffect(() => {
    saveSettings(settings)
  }, [settings])

  // Sync theme with ThemeProvider
  useEffect(() => {
    setThemeContext(settings.theme)
  }, [settings.theme, setThemeContext])

  const setTimezone = useCallback(async (timezone: string) => {
    const newSettings = { ...settings, timezone }
    setSettings(newSettings)

    // Save to database if user is logged in
    if (user?.id) {
      try {
        await saveSettingsToDB(user.id, newSettings)
      } catch (error) {
        console.warn('Failed to save timezone to database:', error)
        // Settings are still updated locally, so user experience isn't broken
      }
    }
  }, [settings, user?.id])

  const setWeekStartsOn = useCallback(async (weekStartsOn: WeekStartsOn) => {
    const newSettings = { ...settings, weekStartsOn }
    setSettings(newSettings)

    // Save to database if user is logged in
    if (user?.id) {
      try {
        await saveSettingsToDB(user.id, newSettings)
      } catch (error) {
        console.warn('Failed to save week_starts_on to database:', error)
        // Settings are still updated locally, so user experience isn't broken
      }
    }
  }, [settings, user?.id])

  const setTheme = useCallback(async (theme: Theme) => {
    const newSettings = { ...settings, theme }
    setSettings(newSettings)

    // Save to database if user is logged in
    if (user?.id) {
      try {
        await saveSettingsToDB(user.id, newSettings)
      } catch (error) {
        console.warn('Failed to save theme to database:', error)
        // Settings are still updated locally, so user experience isn't broken
      }
    }
  }, [settings, user?.id])

  const updateSettings = useCallback(async (newSettings: Partial<{ timezone: string; weekStartsOn: WeekStartsOn; theme: Theme }>) => {
    const updatedSettings = { ...settings, ...newSettings }
    setSettings(updatedSettings)

    // Save to database if user is logged in
    if (user?.id) {
      try {
        await saveSettingsToDB(user.id, updatedSettings)
      } catch (error) {
        console.warn('Failed to save settings to database:', error)
        // Settings are still updated locally, so user experience isn't broken
      }
    }
  }, [settings, user?.id])

  const value = useMemo(() => ({
    timezone: settings.timezone,
    weekStartsOn: settings.weekStartsOn,
    theme: settings.theme,
    loading,
    setTimezone,
    setWeekStartsOn,
    setTheme,
    updateSettings,
  }), [settings.timezone, settings.weekStartsOn, settings.theme, loading, setTimezone, setWeekStartsOn, setTheme, updateSettings])

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}
